import React from 'react';

const STAR_WARS_PATTERN = /(\bStar Wars\b)/gi;

/** Greycliff has no italic axis — the oblique cut is the italic. `not-italic` keeps the browser from slanting it again. */
const DEFAULT_CLASS_NAME = 'font-greycliff-extraboldoblique not-italic';

/**
 * Wraps every "Star Wars" in the Greycliff oblique face so the brand stays italic.
 * Accepts a string or a React tree (for example fancy-underline output).
 * Pass `className` to use another oblique cut, such as `font-greycliff-regularoblique not-italic` in body copy.
 */
export function italicizeStarWars(content: React.ReactNode, className = DEFAULT_CLASS_NAME): React.ReactNode {
  return React.Children.map(content, (child) => renderStarWarsNode(child, className));
}

function renderStarWarsNode(child: React.ReactNode, className: string): React.ReactNode {
  if (typeof child === 'string') {
    return italicizeStarWarsString(child, className);
  }

  if (typeof child === 'number') {
    return italicizeStarWarsString(String(child), className);
  }

  if (!React.isValidElement<{ children?: React.ReactNode }>(child) || child.props.children == null) {
    return child;
  }

  return React.cloneElement(child, {
    children: italicizeStarWars(child.props.children, className),
  });
}

function italicizeStarWarsString(text: string, className: string): React.ReactNode {
  const parts = text.split(STAR_WARS_PATTERN);
  if (parts.length === 1) return text;

  return parts.map((part, index) =>
    part.toLowerCase() === 'star wars' ? (
      <i key={index} className={className}>
        {part}
      </i>
    ) : (
      part
    ),
  );
}
